import { styled } from "goober";
import { playClick } from "../lib/sounds";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { IconBadge } from "./IconBadge";
import { Text } from "./Text";

type Skill = {
  id: string;
  name: string;
  description: string;
};

const Container = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
  &[data-clickable="true"] {
    cursor: pointer;
    &:hover {
      background: var(--alpha4);
    }
  }
  & .row-actions {
    margin-left: auto;
    opacity: 0;
  }
  &:hover .row-actions {
    opacity: 1;
  }
`;

const Info = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

type Props = {
  skills: readonly Skill[];
  onSelect?: (skill: Skill) => void;
  emptyDescription?: string;
  removableIds?: ReadonlySet<string>;
  onRemove?: (skill: Skill) => void;
};

export function SkillsList({
  skills,
  onSelect,
  emptyDescription = "Skills available in this context appear here.",
  removableIds,
  onRemove,
}: Props) {
  if (skills.length === 0) {
    return (
      <Container>
        <Row>
          <IconBadge>
            <Icon name="circle-slash" size={16} />
          </IconBadge>
          <Info>
            <Text variant="text-regular-md" color="var(--text-primary)">
              No skills
            </Text>
            <Text variant="text-small" color="var(--text-secondary)">
              {emptyDescription}
            </Text>
          </Info>
        </Row>
      </Container>
    );
  }

  return (
    <Container>
      {skills.map((skill) => (
        <Row
          key={skill.id}
          data-clickable={onSelect ? "true" : undefined}
          onMouseDown={
            onSelect
              ? () => {
                  playClick();
                  onSelect(skill);
                }
              : undefined
          }
        >
          <IconBadge>
            <Icon name="skills" size={16} />
          </IconBadge>
          <Info>
            <Text
              ellipsis
              variant="text-regular-md"
              color="var(--text-primary)"
            >
              {skill.name || "Untitled skill"}
            </Text>
            <Text ellipsis variant="text-small" color="var(--text-secondary)">
              {skill.description}
            </Text>
          </Info>
          {onRemove && removableIds?.has(skill.id) && (
            <div
              className="row-actions"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <Dropdown
                opener={<Button variant="secondary" icon="more" />}
                align="right"
                minWidth={180}
                sections={[
                  {
                    items: [
                      {
                        type: "button" as const,
                        left: <Icon name="trash" size={16} />,
                        label: "Remove access",
                        onClick: () => onRemove(skill),
                      },
                    ],
                  },
                ]}
              />
            </div>
          )}
        </Row>
      ))}
    </Container>
  );
}
